/**
 * Utility functions for saving and loading filter states to/from localStorage
 */

export interface FilterStorage {
  [key: string]: any;
}

/**
 * Saves filter state to localStorage
 * @param pageName - The page identifier (e.g., 'customers', 'persons', 'projects')
 * @param filters - The filter state object to save
 */
export const saveFiltersToStorage = (pageName: string, filters: FilterStorage): void => {
  try {
    const key = `filter-${pageName}`;
    const serialized = JSON.stringify(filters);
    localStorage.setItem(key, serialized);
  } catch (error) {
    console.error(`Failed to save filters for ${pageName}:`, error);
  }
};

/**
 * Loads filter state from localStorage
 * @param pageName - The page identifier
 * @param defaultFilters - Default filter values to use if no saved state exists
 * @returns The saved filter state or default values
 */
export const loadFiltersFromStorage = <T extends FilterStorage>(
  pageName: string,
  defaultFilters: T
): T => {
  try {
    const key = `filter-${pageName}`;
    const savedFilters = localStorage.getItem(key);

    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      // Merge with defaults to ensure all required fields exist
      return { ...defaultFilters, ...parsedFilters };
    }
  } catch (error) {
    console.error(`Failed to load filters for ${pageName}:`, error);
  }

  return defaultFilters;
};

/**
 * Clears filter state for a specific page
 * @param pageName - The page identifier
 */
export const clearFiltersFromStorage = (pageName: string): void => {
  try {
    const key = `filter-${pageName}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Failed to clear filters for ${pageName}:`, error);
  }
};